import React, { useState, useEffect, useMemo } from 'react';
import {
  Globe,
  Terminal,
  Play,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Database,
  ExternalLink,
  Download,
  Copy,
  Layers,
  Sparkles,
  Sliders,
  ChevronRight,
  X,
  Building,
  Calendar,
  DollarSign,
  Tag,
  Paperclip,
  Check,
  Code2,
  Activity,
  Cpu
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { db, doc, setDoc, serverTimestamp, OperationType, handleFirestoreError } from '../firebase';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { getSafeSolicitationDocId, ensureFirestoreSafeSize } from '../utils/firestoreSanitizer';
import type { PortalRow, UnifiedRfxItem } from '../services/sgpvp/types';
import { Card, Button, cn } from './SaaSUIHelper';

export const SgpvpRetrievalDashboard: React.FC = () => {
  const { addToast } = useSavedSolicitations();

  // Navigation tabs
  const [activeTab, setActiveTab] = useState<'crawler' | 'portals' | 'cli'>('crawler');

  // Portals data
  const [portals, setPortals] = useState<PortalRow[]>([]);
  const [loadingPortals, setLoadingPortals] = useState(true);
  const [portalSearch, setPortalSearch] = useState('');
  const [familyFilter, setFamilyFilter] = useState<string>('all');

  // Crawler state
  const [selectedState, setSelectedState] = useState<string>('Massachusetts');
  const [limit, setLimit] = useState<number>(3);
  const [engine, setEngine] = useState<'playwright' | 'playwright-cli'>('playwright');
  const [fetchDetails, setFetchDetails] = useState<boolean>(true);
  const [isCrawling, setIsCrawling] = useState<boolean>(false);
  const [logs, setLogs] = useState<Array<{ id: string; time: string; msg: string; type: 'info' | 'warn' | 'error' | 'success' }>>([]);
  const [extractedItems, setExtractedItems] = useState<UnifiedRfxItem[]>([]);
  const [selectedItemDetail, setSelectedItemDetail] = useState<UnifiedRfxItem | null>(null);
  const [isSavingToFirestore, setIsSavingToFirestore] = useState(false);

  // CLI Script Modal
  const [cliScriptModalState, setCliScriptModalState] = useState<PortalRow | null>(null);
  const [generatedScript, setGeneratedScript] = useState<string>('');
  const [loadingScript, setLoadingScript] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);

  // Load Portals Catalog
  const fetchPortals = async () => {
    setLoadingPortals(true);
    try {
      const res = await fetch('/api/sgpvp/portals');
      const contentType = res.headers.get('content-type') || '';
      if (res.ok && contentType.includes('application/json')) {
        const data = await res.json();
        setPortals(data.portals || []);
      } else {
        console.warn('Unexpected response format when fetching portals:', res.status, contentType);
      }
    } catch (err) {
      console.warn('Failed to load portals:', err);
    } finally {
      setLoadingPortals(false);
    }
  };

  useEffect(() => {
    fetchPortals();
  }, []);

  // Filtered Portals
  const filteredPortals = useMemo(() => {
    return portals.filter((p) => {
      const matchesSearch =
        p.State.toLowerCase().includes(portalSearch.toLowerCase()) ||
        p.StateCode.toLowerCase().includes(portalSearch.toLowerCase()) ||
        p.ImportSourceUrl.toLowerCase().includes(portalSearch.toLowerCase());
      const matchesFamily = familyFilter === 'all' || p.portalFamily === familyFilter;
      return matchesSearch && matchesFamily;
    });
  }, [portals, portalSearch, familyFilter]);

  // Execute Crawler
  const handleLaunchCrawl = async () => {
    if (isCrawling) return;
    setIsCrawling(true);
    setLogs([]);
    setExtractedItems([]);

    const startTime = Date.now();
    addLog(`Initiating SGPVP RFX Retrieval for "${selectedState}"...`, 'info');
    addLog(`Config: Engine=${engine}, Limit=${limit}, FetchDetails=${fetchDetails}`, 'info');

    try {
      const response = await fetch('/api/sgpvp/retrieve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          state: selectedState,
          limit,
          engine,
          fetchDetails
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Retrieval failed with status ${response.status}` }));
        throw new Error(errorData.error || 'Server error occurred');
      }

      const data = await response.json().catch(() => ({ items: [], statesProcessed: 0 }));
      const items: UnifiedRfxItem[] = data.items || [];
      setExtractedItems(items);

      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      addLog(`✓ Successfully retrieved ${items.length} solicitations from ${data.statesProcessed || 1} portal(s) in ${elapsed}s.`, 'success');

      if (data.errors && data.errors.length > 0) {
        data.errors.forEach((e: any) => addLog(`⚠️ Portal Warning [${e.state}]: ${e.error}`, 'warn'));
      }

      addToast?.({
        title: 'SGPVP Retrieval Complete',
        message: `Extracted ${items.length} open bid solicitations across ${selectedState}.`,
        type: 'success'
      });
    } catch (err: any) {
      addLog(`✖ Retrieval Error: ${err.message}`, 'error');
      addToast?.({
        title: 'Retrieval Interrupted',
        message: err.message,
        type: 'danger'
      });
    } finally {
      setIsCrawling(false);
    }
  };

  const addLog = (msg: string, type: 'info' | 'warn' | 'error' | 'success' = 'info') => {
    const time = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, time, msg, type }]);
  };

  // Sync to Firestore
  const handleSyncToFirestore = async () => {
    if (extractedItems.length === 0 || isSavingToFirestore) return;
    setIsSavingToFirestore(true);

    try {
      let savedCount = 0;
      for (const item of extractedItems) {
        const lineRows: string[][] = [["Line", "Item Name", "Commodity", "Qty", "UOM", "Estimated Cost"]];
        if (item.lineItems && item.lineItems.length > 0) {
          for (const li of item.lineItems) {
            lineRows.push([
              String(li.lineIndex || ""),
              li.itemName || "",
              li.commodityCode || "",
              String(li.quantity || ""),
              li.unitOfMeasure || "",
              String(li.estimatedCost || "")
            ]);
          }
        } else {
          lineRows.push(["1", item.title, item.category || "General", "1", "Each", item.estimatedValue || "N/A"]);
        }

        const firestoreReadyDoc = ensureFirestoreSafeSize({
          solicitationId: item.solicitationId,
          title: item.title,
          state: item.sourceState,
          stateCode: item.sourceStateCode,
          agency: item.agency,
          description: item.description,
          dueDate: item.dueDate || "Open / Rolling",
          postedDate: item.postedDate || new Date().toISOString().split("T")[0],
          status: item.status === "closed" ? "closed" : item.status === "awarded" ? "awarded" : "open",
          sourceUrl: item.detailUrl || item.sourcePortalUrl,
          importSource: item.sourcePortalUrl,
          category: item.category,
          pricing: item.estimatedValue ? parseFloat(item.estimatedValue.replace(/[^0-9.]/g, "")) || undefined : undefined,
          details: {
            importSource: item.sourcePortalUrl,
            generalinformation: {
              "Solicitation Name": item.title,
              "Code": item.solicitationId,
              "Round ": "1",
              "Begin": item.postedDate || "Current",
              "Buyer": item.buyer || "Procurement Officer",
              "Buyer Contact": item.buyerEmail || item.buyerPhone || "See Portal",
              "Location": item.location || item.sourceState,
              ...item.generalInformation
            },
            response: { rows: lineRows },
            solicitationdocuments: (item.documents || []).map((doc, idx) => ({
              name: doc.name || `Document_${idx + 1}`,
              url: doc.url,
              version: typeof doc.version === 'number' ? doc.version : 1
            }))
          },
          tags: item.tags || []
        });

        await setDoc(doc(db, 'solicitations', getSafeSolicitationDocId(item.solicitationId)), {
          ...firestoreReadyDoc,
          createdAt: serverTimestamp()
        });
        savedCount++;
      }

      addToast?.({
        title: 'Saved to RFX Database',
        message: `Successfully synchronized ${savedCount} solicitations to Firestore.`,
        type: 'success'
      });
    } catch (err: any) {
      console.error('Firestore save failed:', err);
      addToast?.({
        title: 'Database Save Failed',
        message: err.message,
        type: 'danger'
      });
    } finally {
      setIsSavingToFirestore(false);
    }
  };

  // Generate CLI Script for a portal
  const handleOpenCliScript = async (portal: PortalRow) => {
    setCliScriptModalState(portal);
    setLoadingScript(true);
    setCopiedScript(false);

    try {
      const res = await fetch('/api/sgpvp/cli-script', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: portal.State, limit: 5 })
      });
      const data = await res.json().catch(() => ({ script: '' }));
      setGeneratedScript(data.script || '');
    } catch (err) {
      console.error('Script generation error:', err);
    } finally {
      setLoadingScript(false);
    }
  };

  // Export JSON
  const handleExportJson = () => {
    if (extractedItems.length === 0) return;
    const blob = new Blob([JSON.stringify(extractedItems, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sgpvp-rfx-${selectedState.toLowerCase()}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-8 text-white shadow-xl border border-indigo-900/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-3 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-xs font-semibold uppercase tracking-wider">
              <Globe className="w-3.5 h-3.5" />
              SGPVP Multi-State Procurement Harvester
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              State Government Procurement Vendor Portals
            </h1>
            <p className="text-slate-300 text-sm leading-relaxed">
              Autonomous Playwright &amp; Playwright-CLI crawler engine supporting 53 official state procurement portals.
              Extracts live open solicitations, detailed scopes of work, itemized bill of quantities, and attachments mapped to the Unified RFX Schema.
            </p>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-3 gap-3 bg-white/5 backdrop-blur-md p-4 rounded-2xl border border-white/10 shrink-0">
            <div className="text-center px-3 border-r border-white/10">
              <p className="text-xs text-slate-400 font-medium">State Portals</p>
              <p className="text-2xl font-black text-white">{portals.length || 53}</p>
            </div>
            <div className="text-center px-3 border-r border-white/10">
              <p className="text-xs text-slate-400 font-medium">Adapters</p>
              <p className="text-2xl font-black text-indigo-400">7 Families</p>
            </div>
            <div className="text-center px-3">
              <p className="text-xs text-slate-400 font-medium">Engine Mode</p>
              <p className="text-2xl font-black text-emerald-400">Playwright</p>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mt-8 pt-6 border-t border-white/10">
          <button
            onClick={() => setActiveTab('crawler')}
            className={cn(
              'px-5 py-2.5 rounded-xl font-semibold text-xs transition-all flex items-center gap-2 cursor-pointer',
              activeTab === 'crawler'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            )}
          >
            <Play className="w-4 h-4" />
            Live RFX Crawler
          </button>
          <button
            onClick={() => setActiveTab('portals')}
            className={cn(
              'px-5 py-2.5 rounded-xl font-semibold text-xs transition-all flex items-center gap-2 cursor-pointer',
              activeTab === 'portals'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            )}
          >
            <Layers className="w-4 h-4" />
            Portal Catalog ({portals.length})
          </button>
          <button
            onClick={() => setActiveTab('cli')}
            className={cn(
              'px-5 py-2.5 rounded-xl font-semibold text-xs transition-all flex items-center gap-2 cursor-pointer',
              activeTab === 'cli'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            )}
          >
            <Terminal className="w-4 h-4" />
            Playwright-CLI Studio
          </button>
        </div>
      </div>

      {/* Tab 1: Live RFX Crawler */}
      {activeTab === 'crawler' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Controls & Configuration */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl space-y-6">
              <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-indigo-600" />
                  Crawler Parameters
                </h3>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                  Ready
                </span>
              </div>

              {/* State Selection */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-gray-700">Target State Portal</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  disabled={isCrawling}
                  className="w-full px-3 py-2.5 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                >
                  <option value="All">All 53 State Portals (Batch Run)</option>
                  {portals.map((p) => (
                    <option key={p.StateCode} value={p.State}>
                      {p.State} ({p.StateCode}) - {p.portalFamily}
                    </option>
                  ))}
                </select>
              </div>

              {/* Engine Selection */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-gray-700">Execution Engine</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEngine('playwright')}
                    className={cn(
                      'p-3 rounded-xl border text-left text-xs transition-all cursor-pointer',
                      engine === 'playwright'
                        ? 'border-indigo-600 bg-indigo-50/50 text-indigo-900 font-bold'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    <Cpu className="w-4 h-4 mb-1 text-indigo-600" />
                    Playwright Core
                    <span className="block text-[10px] font-normal text-gray-500">Fast headless session</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEngine('playwright-cli')}
                    className={cn(
                      'p-3 rounded-xl border text-left text-xs transition-all cursor-pointer',
                      engine === 'playwright-cli'
                        ? 'border-indigo-600 bg-indigo-50/50 text-indigo-900 font-bold'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    <Terminal className="w-4 h-4 mb-1 text-indigo-600" />
                    Playwright-CLI
                    <span className="block text-[10px] font-normal text-gray-500">Terminal bridge mode</span>
                  </button>
                </div>
              </div>

              {/* Limit Slider */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="font-semibold text-gray-700">Items per Portal</span>
                  <span className="font-bold text-indigo-600">{limit} items</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  disabled={isCrawling}
                  className="w-full accent-indigo-600 cursor-pointer"
                />
              </div>

              {/* Detail Page Checkbox */}
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fetchDetails}
                  onChange={(e) => setFetchDetails(e.target.checked)}
                  disabled={isCrawling}
                  className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                />
                <div className="text-xs">
                  <span className="font-semibold text-gray-900 block">Deep Detail Extraction</span>
                  <span className="text-[10px] text-gray-500">Navigates to detail pages for line items &amp; attachments</span>
                </div>
              </label>

              {/* Launch Button */}
              <Button
                onClick={handleLaunchCrawl}
                disabled={isCrawling}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 cursor-pointer transition-all"
              >
                {isCrawling ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Extracting Open Solicitations...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 fill-current" />
                    Launch SGPVP Retrieval
                  </>
                )}
              </Button>
            </Card>

            {/* Live Terminal Stream */}
            <div className="bg-slate-950 text-slate-300 rounded-2xl p-4 border border-slate-800 font-mono text-[11px] space-y-3 shadow-inner">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-1.5">
                  <Terminal className="w-3.5 h-3.5" />
                  Crawler Console Stream
                </span>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>

              <div className="h-56 overflow-y-auto space-y-1.5 pr-2">
                {logs.length === 0 ? (
                  <p className="text-slate-600 italic">Ready. Click "Launch SGPVP Retrieval" to start scraping live portals.</p>
                ) : (
                  logs.map((log) => (
                    <div key={log.id} className="flex items-start gap-2">
                      <span className="text-slate-600 shrink-0">[{log.time}]</span>
                      <span
                        className={cn(
                          'break-all',
                          log.type === 'error' && 'text-rose-400 font-bold',
                          log.type === 'warn' && 'text-amber-400',
                          log.type === 'success' && 'text-emerald-400 font-bold',
                          log.type === 'info' && 'text-slate-300'
                        )}
                      >
                        {log.msg}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Extracted RFX Items Results */}
          <div className="lg:col-span-8 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
              <div>
                <h3 className="font-extrabold text-gray-900 text-base flex items-center gap-2">
                  <Database className="w-5 h-5 text-indigo-600" />
                  Unified RFX Items
                  <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700">
                    {extractedItems.length} Solicitations
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Standardized schema mapping from state-specific procurement formats.
                </p>
              </div>

              {extractedItems.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportJson}
                    className="px-3.5 py-2 rounded-xl text-xs font-semibold border border-gray-200 hover:bg-gray-50 text-gray-700 flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export JSON
                  </button>
                  <button
                    onClick={handleSyncToFirestore}
                    disabled={isSavingToFirestore}
                    className="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 shadow-md shadow-emerald-600/20 transition-all cursor-pointer"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {isSavingToFirestore ? 'Saving...' : 'Sync to RFX Database'}
                  </button>
                </div>
              )}
            </div>

            {/* Items Cards */}
            {extractedItems.length === 0 ? (
              <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-3xl p-12 text-center space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
                  <Search className="w-6 h-6" />
                </div>
                <div className="space-y-1 max-w-sm mx-auto">
                  <h4 className="font-bold text-gray-900 text-sm">No Solicitations Harvested Yet</h4>
                  <p className="text-xs text-gray-500">
                    Configure your parameters on the left and trigger the scraper to inspect live bids from state portals.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {extractedItems.map((item, idx) => (
                  <motion.div
                    key={item.solicitationId || idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all space-y-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2.5 py-0.5 rounded-md text-[10px] font-extrabold bg-indigo-50 text-indigo-700 uppercase tracking-wider">
                            {item.sourceState} ({item.sourceStateCode})
                          </span>
                          <span className="px-2.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 uppercase">
                            {item.status}
                          </span>
                          <span className="text-xs font-mono text-gray-400">
                            ID: {item.solicitationId}
                          </span>
                        </div>
                        <h4 className="text-base font-bold text-gray-900 leading-snug">
                          {item.title}
                        </h4>
                        <p className="text-xs text-gray-500 font-medium flex items-center gap-2">
                          <Building className="w-3.5 h-3.5 text-gray-400" />
                          {item.agency}
                        </p>
                      </div>

                      <button
                        onClick={() => setSelectedItemDetail(item)}
                        className="px-3.5 py-2 text-xs font-semibold bg-gray-100 hover:bg-indigo-50 hover:text-indigo-600 text-gray-700 rounded-xl transition-colors flex items-center gap-1.5 self-start cursor-pointer"
                      >
                        View Full Details
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>

                    {/* Metadata Pills */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs text-gray-500 flex-wrap gap-3">
                      <div className="flex items-center gap-4">
                        {item.dueDate && (
                          <span className="flex items-center gap-1.5 font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md">
                            <Calendar className="w-3.5 h-3.5 text-amber-500" />
                            Due: {item.dueDate}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 font-medium text-gray-600">
                          <Tag className="w-3.5 h-3.5 text-gray-400" />
                          {item.category}
                        </span>
                        {item.lineItems && item.lineItems.length > 0 && (
                          <span className="flex items-center gap-1.5 font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                            <Layers className="w-3.5 h-3.5" />
                            {item.lineItems.length} Line Items
                          </span>
                        )}
                        {item.documents && item.documents.length > 0 && (
                          <span className="flex items-center gap-1.5 font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md">
                            <Paperclip className="w-3.5 h-3.5" />
                            {item.documents.length} Files
                          </span>
                        )}
                      </div>

                      {item.detailUrl && (
                        <a
                          href={item.detailUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1"
                        >
                          Source Portal
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Portals Catalog */}
      {activeTab === 'portals' && (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by state name, code, or portal URL..."
                value={portalSearch}
                onChange={(e) => setPortalSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-600">Adapter Family:</span>
              <select
                value={familyFilter}
                onChange={(e) => setFamilyFilter(e.target.value)}
                className="px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
              >
                <option value="all">All Families</option>
                <option value="ivalua">Ivalua (AL, AZ, MD, OH)</option>
                <option value="cgiadvantage">CGI Advantage (AK, CO, KY, VA, WV)</option>
                <option value="periscope">Periscope / BuySpeed (IL, MA, NJ, OR, MO)</option>
                <option value="jaggaer">Jaggaer / SciQuest (IA, MT, NM, UT)</option>
                <option value="ionwave">IonWave (NV, SD)</option>
                <option value="custom-tx">Custom TX</option>
                <option value="custom-ca">Custom CA</option>
                <option value="generic">Universal Generic</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPortals.map((portal) => (
              <div
                key={portal.StateCode}
                className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all space-y-3 flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-extrabold text-gray-900">
                      {portal.State}
                    </span>
                    <span className="text-xs font-mono font-bold bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">
                      {portal.StateCode}
                    </span>
                  </div>

                  <span className="inline-block px-2.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 uppercase tracking-wider">
                    {portal.portalFamily}
                  </span>

                  <p className="text-xs text-gray-500 truncate font-mono" title={portal.ImportSourceUrl}>
                    {portal.ImportSourceUrl}
                  </p>
                </div>

                <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                  <a
                    href={portal.ImportSourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1"
                  >
                    Open Portal
                    <ExternalLink className="w-3 h-3" />
                  </a>

                  <button
                    onClick={() => handleOpenCliScript(portal)}
                    className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Code2 className="w-3.5 h-3.5" />
                    CLI Script
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 3: Playwright-CLI Studio */}
      {activeTab === 'cli' && (
        <Card className="p-8 bg-white border border-gray-200 rounded-3xl shadow-sm space-y-6">
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Terminal className="w-6 h-6 text-indigo-600" />
              Playwright-CLI Automated Script Studio
            </h3>
            <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
              Generate reproducible command-line scripts using the official `playwright-cli` tool.
              You can run these scripts directly in your bash terminal or embed them in automated cron jobs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-4 space-y-4">
              <label className="block text-xs font-semibold text-gray-700">Choose State to Generate Script</label>
              <select
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value);
                  const p = portals.find((x) => x.State === e.target.value);
                  if (p) handleOpenCliScript(p);
                }}
                className="w-full px-3 py-2.5 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
              >
                {portals.map((p) => (
                  <option key={p.StateCode} value={p.State}>
                    {p.State} ({p.StateCode})
                  </option>
                ))}
              </select>

              <div className="p-4 bg-indigo-50/60 rounded-2xl border border-indigo-100 text-xs text-indigo-900 space-y-2">
                <p className="font-bold flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  CLI Quick Start
                </p>
                <p className="text-[11px] leading-relaxed text-indigo-800">
                  Run directly from your workspace:
                </p>
                <pre className="bg-indigo-950 text-indigo-200 p-2.5 rounded-lg text-[10px] font-mono overflow-x-auto">
                  npx tsx scripts/sgpvp/retrieve-rfx-items.ts --state "{selectedState}" --limit 5
                </pre>
              </div>
            </div>

            <div className="lg:col-span-8 bg-slate-950 p-6 rounded-2xl border border-slate-800 text-slate-300 font-mono text-xs space-y-4 shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <span className="text-indigo-400 font-bold flex items-center gap-2">
                  <Code2 className="w-4 h-4" />
                  playwright-cli-workflow.sh
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedScript);
                    setCopiedScript(true);
                    setTimeout(() => setCopiedScript(false), 2000);
                  }}
                  className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded-md text-[11px] font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  {copiedScript ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedScript ? 'Copied!' : 'Copy Script'}
                </button>
              </div>

              <pre className="overflow-x-auto text-[11px] leading-relaxed text-emerald-400 whitespace-pre-wrap max-h-[380px]">
                {loadingScript ? '// Generating script from portal registry...' : generatedScript || '// Select a state above to generate script.'}
              </pre>
            </div>
          </div>
        </Card>
      )}

      {/* Solicitation Detail Modal */}
      <AnimatePresence>
        {selectedItemDetail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden border border-gray-200"
            >
              {/* Modal Header */}
              <div className="p-6 bg-slate-900 text-white flex items-start justify-between gap-4 shrink-0">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-600 text-white">
                      {selectedItemDetail.sourceState}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      ID: {selectedItemDetail.solicitationId}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-white leading-snug">
                    {selectedItemDetail.title}
                  </h3>
                  <p className="text-xs text-slate-300">
                    {selectedItemDetail.agency} • {selectedItemDetail.buyer || 'State Buyer'}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedItemDetail(null)}
                  className="p-1.5 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-6 text-xs text-gray-700">
                {/* Critical Dates & Overview */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <span className="text-[10px] text-gray-400 uppercase font-bold block">Status</span>
                    <span className="font-bold text-emerald-600 text-sm capitalize">{selectedItemDetail.status}</span>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <span className="text-[10px] text-gray-400 uppercase font-bold block">Response Due</span>
                    <span className="font-bold text-gray-900 text-xs">{selectedItemDetail.dueDate || 'Open'}</span>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <span className="text-[10px] text-gray-400 uppercase font-bold block">Category</span>
                    <span className="font-bold text-gray-900 text-xs">{selectedItemDetail.category}</span>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <span className="text-[10px] text-gray-400 uppercase font-bold block">Estimated Budget</span>
                    <span className="font-bold text-indigo-600 text-xs">{selectedItemDetail.estimatedValue || 'Unspecified'}</span>
                  </div>
                </div>

                {/* Scope of Work */}
                <div className="space-y-2">
                  <h4 className="font-bold text-gray-900 uppercase tracking-wider text-[11px]">
                    Scope of Work &amp; Description
                  </h4>
                  <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {selectedItemDetail.description}
                  </div>
                </div>

                {/* Line Items Table */}
                {selectedItemDetail.lineItems && selectedItemDetail.lineItems.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-bold text-gray-900 uppercase tracking-wider text-[11px]">
                      Itemized Line Items ({selectedItemDetail.lineItems.length})
                    </h4>
                    <div className="overflow-x-auto border border-gray-200 rounded-xl">
                      <table className="w-full text-left">
                        <thead className="bg-gray-100 text-gray-600 font-semibold border-b border-gray-200">
                          <tr>
                            <th className="p-2.5">#</th>
                            <th className="p-2.5">Item Name / Description</th>
                            <th className="p-2.5">Qty</th>
                            <th className="p-2.5">UOM</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {selectedItemDetail.lineItems.map((li, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="p-2.5 font-mono text-gray-400">{li.lineIndex || i + 1}</td>
                              <td className="p-2.5 font-medium text-gray-900">{li.itemName}</td>
                              <td className="p-2.5">{li.quantity || '1'}</td>
                              <td className="p-2.5">{li.unitOfMeasure || 'EA'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Downloadable Documents */}
                {selectedItemDetail.documents && selectedItemDetail.documents.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-bold text-gray-900 uppercase tracking-wider text-[11px]">
                      Attachments &amp; Specifications ({selectedItemDetail.documents.length})
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {selectedItemDetail.documents.map((doc, idx) => (
                        <a
                          key={idx}
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-3 bg-gray-50 hover:bg-indigo-50 border border-gray-200 rounded-xl flex items-center justify-between text-gray-800 font-medium transition-colors"
                        >
                          <span className="truncate max-w-[240px] flex items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-600 shrink-0" />
                            {doc.name}
                          </span>
                          <Download className="w-3.5 h-3.5 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between shrink-0">
                <a
                  href={selectedItemDetail.detailUrl || selectedItemDetail.sourcePortalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5"
                >
                  Visit State Portal Page
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>

                <Button
                  onClick={() => setSelectedItemDetail(null)}
                  className="px-5 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-semibold text-xs cursor-pointer"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

